/**
 * SSOT for agent session on blueprint APIs (/api/agents/me/*, /api/agent/*).
 * Same precedence as authService.getSessionId and fhpService _attachAgentSessionToBody.
 */
import { getSessionId, getUserDetails } from "./authService";
import { getUserInfo } from "./localStorage";

/**
 * Synchronous session resolution (UserInfo → localStorage sessionID key).
 * @returns {string|null}
 */
export function resolveAgentSessionIdSync() {
  try {
    const u = getUserInfo() || getUserDetails();
    if (u && typeof u === "object") {
      const fromUser = u.sessionID ?? u.sessionId;
      if (fromUser != null && String(fromUser).trim() !== "") {
        return String(fromUser).trim();
      }
    }
  } catch {
    /* ignore */
  }
  if (typeof localStorage !== "undefined") {
    const ls = localStorage.getItem("sessionID");
    if (ls != null && String(ls).trim() !== "") {
      return String(ls).trim();
    }
  }
  return null;
}

/**
 * @returns {Promise<string|null>}
 */
export async function resolveAgentSessionId() {
  const sync = resolveAgentSessionIdSync();
  if (sync) return sync;
  const raw = await getSessionId();
  if (!raw || raw === false) return null;
  const sid = String(raw).trim();
  return sid || null;
}

/**
 * @param {Record<string, unknown>} [body]
 * @returns {Record<string, unknown>}
 */
export function attachAgentSessionToBody(body = {}) {
  const sid = resolveAgentSessionIdSync();
  if (!sid) {
    return body && typeof body === "object" && !Array.isArray(body) ? { ...body } : {};
  }
  const base =
    body && typeof body === "object" && !Array.isArray(body) ? { ...body } : {};
  return { ...base, sessionID: sid };
}

/**
 * Axios config + sid for authenticated agent API calls.
 * @param {{ timeout?: number }} [opts]
 */
export async function buildAgentSessionRequestConfig(opts = {}) {
  const sid = await resolveAgentSessionId();
  if (!sid) {
    throw new Error("agent_no_session");
  }
  const timeout = opts.timeout ?? 20000;
  return {
    sid,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Session-ID": sid,
      Authorization: `Bearer ${sid}`,
    },
    withCredentials: true,
    timeout,
  };
}

/** @param {object|null|undefined} user — AppContext state.user or getUserDetails() */
export function agentUserHasResolvableSession(user) {
  if (user && typeof user === "object") {
    const fromUser = user.sessionID ?? user.sessionId;
    if (fromUser != null && String(fromUser).trim() !== "") {
      return true;
    }
  }
  return Boolean(resolveAgentSessionIdSync());
}
